import {
  composeClassNames,
  ifDefined,
  universalEach,
  withDefault,
} from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { CSSProperties, ReactNode, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import Slider from "react-slick";
import Content from "src/components/Content";
import Heading from "src/components/Heading";
import InstructorCard from "src/components/InstructorCard";
import SliderArrow from "src/components/SliderArrow";
import Translate from "src/components/Translate";
import constants from "src/constants";
import validateContent from "src/functions/validateContent";
import { sButton } from "src/styles/sButton.module.scss";
import { sGroupTitle } from "src/styles/sGroupTitle.module.scss";
import { sInstructorSlider } from "src/styles/sInstructorSlider.module.scss";
import { ApiProfileCard } from "src/types/ApiProfileCard";

type Props = {
  title?: ReactNode | null | undefined;
  subtitle?: ReactNode | null | undefined;
  data: ApiProfileCard[];
  style?: CSSProperties | null | undefined;
  className?: (string | string) | null | undefined;
};

function InstructorSlider({ title, subtitle, data, style, className }: Props) {
  const { locale: lang, push } = useRouter();
  const [numberOfInstructors, setNumberOfInstructors] = useState(0);

  useEffect(() => {
    fetch(`${constants.Paths.apiUrl}/profiles/count`, {
      method: "GET",
    }).then(async (res) => {
      if (res.ok) {
        setNumberOfInstructors(await res.json());
      }
    });
  }, []);

  return (
    <Content
      style={style}
      className={composeClassNames([
        sInstructorSlider,
        className,
        "instructor-slider-wrapper",
      ])}
      fluid={`xxl`}
      verticalSpace={`none`}
    >
      {title ? (
        <Heading
          type={`h2`}
          title={title}
          className={composeClassNames([
            sGroupTitle,
            "instructor-slider-title",
          ])}
        />
      ) : null}
      {subtitle && <p className={"instructor-slider-subtitle"}>{subtitle}</p>}
      {data && data.length > 0 && (
        <Slider
          arrows={true}
          infinite={true}
          speed={500}
          nextArrow={<SliderArrow direction={`next`} />}
          prevArrow={<SliderArrow direction={`prev`} />}
          slidesToShow={6}
          slidesToScroll={6}
          responsive={[
            {
              breakpoint: constants.Breakpoints.xl,
              settings: { slidesToShow: 5, slidesToScroll: 5 },
            },
            {
              breakpoint: constants.Breakpoints.lg,
              settings: { slidesToShow: 4, slidesToScroll: 4 },
            },
            {
              breakpoint: constants.Breakpoints.md,
              settings: { slidesToShow: 3, slidesToScroll: 3 },
            },
            {
              breakpoint: constants.Breakpoints.sm,
              settings: { slidesToShow: 2, slidesToScroll: 2 },
            },
          ]}
        >
          {data.length === 0 && <Translate tKey={"No instructors found"} />}
          {(() => {
            let validated = false;
            const instructors = constants.List.hpInstructors;
            const sortedInstructors = instructors.map((instructorPosition) =>
              data.find(
                (instructorId) => Number(instructorId.id) === instructorPosition
              )
            );
            return universalEach(sortedInstructors, (item, i) => {
              const instructor = instructors.find(
                (instructor) => instructor === Number(item?.id)
              );
              validated =
                instructor !== undefined &&
                validateContent(
                  withDefault(item?.blocked, true),
                  ifDefined(
                    item?.verified,
                    (value) => value.verified,
                    () => false
                  ),
                  withDefault(item?.private, false)
                );
              return validated ? (
                <InstructorCard
                  data={item}
                  key={`instructor-card-` + String(item.id)}
                />
              ) : null;
            });
          })()}
        </Slider>
      )}
      <div className={"show-all"}>
        <Button
          onClick={() => push("/profiles")}
          className={composeClassNames([sButton])}
        >
          <Translate tKey={`Explore inspiring instructors`} /> (
          {numberOfInstructors} <Translate tKey={`profiles`} />)
        </Button>
      </div>
    </Content>
  );
}

export default InstructorSlider;
